import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import FormField from 'components/FormField';
import { getText } from 'utils/intl';
import Grid from '@mui/material/Grid2';
import ReportDndElement from 'components/Report/components/ReportDnD';
import logger from 'utils/logger';
import IconButton from '@mui/material/IconButton';
import ReportDropZone from 'components/Report/components/ReportDropZone';
import DeleteIcon from '@mui/icons-material/Delete';
import { CustomDatePicker } from 'components/CustomDatePicker';
import { zIndexValues } from 'utils/styles';
import { getPresignedURL } from 'utils/ReportUtils';
import PropTypes from 'prop-types';

const log = logger.createLogger('ReportEditorComponents');

export const createNumberField = (index, field, handleChange) => {
  return (
    <Grid size={field.isLarge ? 12 : 6} key={`number-${index}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
          {getText(field.label)}
        </Typography>
        <FormField
          variant="outlined"
          type="number"
          value={field.value}
          max={field.max}
          min={field.min}
          onChange={handleChange(field.id)}
          sx={{ flexGrow: 1 }}
        />
      </Box>
    </Grid>
  );
};

export const createSelectField = (index, field, handleChange) => {
  return (
    <Grid size={field.isLarge ? 12 : 6} key={`select-${index}`}>
      <FormControl fullWidth variant="outlined">
        <InputLabel id={`${field.label}-label`}>
          {getText(field.label)}
        </InputLabel>
        <Select
          labelId={`${field.label}-label`}
          value={field.value || ''}
          onChange={handleChange(field.id)}
          label={getText(field.label)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
            sx: {
              zIndex: zIndexValues.iconPopover,
            },
          }}>
          {field.selectSettings?.elements?.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {getText(option.label) || option.value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
};

export const createImageField = (
  index,
  field,
  handleImages,
  handleKeyImageDelete
) => {
  return (
    <Grid size={12} key={`image-${index}`}>
      <Grid
        size={12}
        style={{ marginTop: 15, paddingTop: 12, paddingBottom: 12 }}>
        <MDBox>
          <MDTypography>{getText(field.label)}</MDTypography>
        </MDBox>
      </Grid>
      {field.value.map((item, index) => {
        return (
          <Grid size={12} key={index}>
            <Grid container alignItems="center">
              <Grid size={1}>
                <IconButton
                  onClick={() => {
                    handleKeyImageDelete(item, index);
                  }}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
              <Grid size={11} sx={{ paddingLeft: 1 }}>
                <MDTypography variant="body2">{item.fileName}</MDTypography>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
      <Grid container spacing={3}>
        <Grid size={12} style={{ marginTop: 15 }}>
          <ReportDropZone handleFiles={handleImages} isDisabled={false} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const createTextField = (index, field, handleChange) => {
  const { textSettings } = field;

  return (
    <Grid size={field.isLarge ? 12 : 6} key={`text-${index}`}>
      <FormField
        variant="outlined"
        label={getText(field.label)}
        value={field.value}
        onChange={handleChange(field.id)}
        multiline={textSettings.multiline || false}
        rows={textSettings.multiline ? 3 : undefined}
      />
    </Grid>
  );
};

export const createDateField = (index, field, handleChange) => {
  return (
    <Grid size={field.isLarge ? 12 : 6} key={`date-${index}`}>
      <CustomDatePicker
        selectedDate={field.value || new Date()}
        setSelectedDate={handleChange(field.id)}
        labelText={getText(field.label)}
        fullWidth={true}
      />
    </Grid>
  );
};

export const CreateDndField = ({
  reportTemplateId,
  index,
  field,
  handleCoordinatesChange,
  placedElements,
}) => {
  const [draggableElements, setDraggableElements] = useState([]);
  const [reportTemplateSource, setReportTemplateSource] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { dndSettings } = field;

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Load the main template source
        const templateSource = await getPresignedURL(
          reportTemplateId,
          field.value
        );

        setReportTemplateSource(templateSource);
        // Load all draggable elements
        const elements = await Promise.all(
          dndSettings.elements.map(async (setting, index) => {
            const signedUrl = await getPresignedURL(
              reportTemplateId,
              setting.value
            );

            return {
              id: `dnd-${index}`,
              type: setting.type,
              label: setting.label,
              imageUrl: signedUrl,
              imagePath: setting.value,
              width: setting.width,
              height: setting.height,
            };
          })
        );

        setDraggableElements(elements);
      } catch (error) {
        log.error('Error loading DnD field data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [reportTemplateId, field.value, dndSettings.elements]);

  if (isLoading) {
    return (
      <Grid size={12} key={`dnd-${index}`}>
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={300} />
        </Box>
      </Grid>
    );
  }

  return (
    <Grid size={12} key={`dnd-${index}`}>
      <ReportDndElement
        reportTemplateId={reportTemplateId}
        fieldId={field.id}
        title={field.label}
        imageUrl={reportTemplateSource}
        onCoordinatesChange={handleCoordinatesChange}
        draggableElements={draggableElements}
        placedElements={placedElements}
        containerWidth={field.width}
        containerHeight={field.height}
        readOnly={false}
        pageIndex={field.pageIndex}
      />
    </Grid>
  );
};

CreateDndField.propTypes = {
  reportTemplateId: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  field: PropTypes.object.isRequired,
  handleCoordinatesChange: PropTypes.func.isRequired,
  placedElements: PropTypes.array.isRequired,
};
